import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, ShieldCheck, CheckCircle2, Loader2, ChevronDown } from "lucide-react";
import axios from "axios";
import { API, BRAND, TOPICS } from "@/lib/site";
import { Eyebrow, Reveal } from "./bits";

const initial = { name: "", phone: "", email: "", topic: "", message: "" };

const Field = ({ label, htmlFor, error, errorId, children }) => (
  <div className="group">
    <label
      htmlFor={htmlFor}
      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E8698] transition-colors duration-300 group-focus-within:text-emerald-600"
    >
      {label}
    </label>
    {children}
    <AnimatePresence>
      {error && (
        <motion.p
          data-testid={errorId}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mt-2 text-xs text-red-500"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const inputClass =
  "mt-2 w-full border-b border-[#10202E]/20 bg-transparent py-3 text-[#10202E] placeholder:text-[#8AA0B0] transition-colors duration-300 focus:border-emerald-600 focus:outline-none";

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-4">
    <span className="mt-0.5 rounded-full border border-[#10202E]/10 bg-[#DDF7EC] p-2.5">
      <Icon className="h-4 w-4 text-emerald-600" />
    </span>
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E8698]">{label}</p>
      <p data-testid={testId} className="mt-1 text-base text-[#1C3347]">
        {value}
      </p>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name";
    if (!/^[0-9+\-\s()]{8,20}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.topic) e.topic = "Please choose a topic";
    if (form.message.trim().length < 5) e.message = "Tell us a little more about your goal";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden py-28 md:py-40">
      <div className="animate-glow pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(16,185,129,0.10),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
          <div>
            <Reveal>
              <Eyebrow>11 — Contact</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                Your financial journey starts with{" "}
                <span className="italic text-emerald-600">one conversation.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-md text-lg font-light leading-relaxed text-[#4E6A82]">
                Tell us where you want to go. We'll help you understand the path.
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-12 space-y-7">
                <InfoRow icon={Phone} label="Phone" value={BRAND.phone} testId="contact-phone" />
                <InfoRow icon={Mail} label="Email" value={BRAND.email} testId="contact-email-text" />
                <InfoRow icon={MapPin} label="Registered Office" value={BRAND.address} testId="contact-address" />
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 space-y-2 border-t border-[#10202E]/10 pt-8">
                <p data-testid="contact-gstin" className="font-mono text-[11px] tracking-[0.2em] text-[#6E8698]">
                  GSTIN — {BRAND.gstin}
                </p>
                <p data-testid="contact-arn" className="font-mono text-[11px] tracking-[0.2em] text-[#6E8698]">
                  AMFI Registration — {BRAND.arn}
                </p>
                <p className="flex items-center gap-2 pt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {BRAND.badge}
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="relative rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_20px_60px_rgba(16,32,46,0.08)] sm:p-10">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <span className="rounded-full border border-emerald-600/30 bg-[#DDF7EC] p-5">
                      <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                    </span>
                    <h3 className="mt-6 font-display text-3xl text-[#10202E]">Thank you.</h3>
                    <p className="mt-3 max-w-sm text-sm font-light leading-relaxed text-[#4E6A82]">
                      Your message has been received. We'll reach out within one business day to
                      begin the conversation.
                    </p>
                    <button
                      type="button"
                      data-testid="contact-send-another"
                      onClick={() => {
                        setForm(initial);
                        setStatus("idle");
                      }}
                      className="mt-8 rounded-full border border-[#10202E]/20 px-6 py-3 text-sm font-medium text-[#1C3347] transition-colors duration-300 hover:border-emerald-600/50 hover:text-emerald-700"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={submit}
                    noValidate
                    className="space-y-7"
                  >
                    <div className="grid gap-7 sm:grid-cols-2">
                      <Field label="Name" htmlFor="contact-name" error={errors.name} errorId="contact-name-error">
                        <input
                          id="contact-name"
                          data-testid="contact-name-input"
                          type="text"
                          value={form.name}
                          onChange={set("name")}
                          placeholder="Your full name"
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Phone" htmlFor="contact-phone" error={errors.phone} errorId="contact-phone-error">
                        <input
                          id="contact-phone"
                          data-testid="contact-phone-input"
                          type="tel"
                          value={form.phone}
                          onChange={set("phone")}
                          placeholder="+91 ..."
                          className={inputClass}
                        />
                      </Field>
                    </div>

                    <Field label="Email" htmlFor="contact-email" error={errors.email} errorId="contact-email-error">
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        className={inputClass}
                      />
                    </Field>

                    <Field label="What would you like help with?" htmlFor="contact-topic" error={errors.topic} errorId="contact-topic-error">
                      <div className="relative">
                        <select
                          id="contact-topic"
                          data-testid="contact-topic-select"
                          value={form.topic}
                          onChange={set("topic")}
                          className={`${inputClass} appearance-none pr-10 ${form.topic ? "" : "text-[#8AA0B0]"}`}
                        >
                          <option value="" disabled className="bg-white">
                            Choose a topic
                          </option>
                          {TOPICS.map((t) => (
                            <option key={t} value={t} className="bg-white text-[#10202E]">
                              {t}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 mt-1 h-4 w-4 -translate-y-1/2 text-[#8AA0B0]" />
                      </div>
                    </Field>

                    <Field label="Message" htmlFor="contact-message" error={errors.message} errorId="contact-message-error">
                      <textarea
                        id="contact-message"
                        data-testid="contact-message-input"
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us about your goals, or where you are today..."
                        className={`${inputClass} resize-none`}
                      />
                    </Field>

                    {status === "error" && (
                      <p data-testid="contact-error" className="text-sm text-red-500">
                        Something went wrong while sending. Please try again, or email us directly at{" "}
                        {BRAND.email}.
                      </p>
                    )}

                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={status === "sending"}
                      className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-emerald-400 px-7 py-4 text-sm font-semibold tracking-wide text-[#04120c] transition-[background-color,box-shadow] duration-300 hover:bg-emerald-300 hover:shadow-[0_8px_40px_rgba(16,185,129,0.35)] disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Let's talk"
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
